import * as R from 'ramda';
import { useMemo } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
// mui
import Typography from '@mui/material/Typography';
// icons-material
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpIcon from '@mui/icons-material/ArrowUpwardOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowDownIcon from '@mui/icons-material/ArrowDownwardOutlined';
// utilities
import * as U from '../../../../utilities';
// ui
import { Box, Flex } from '../../../../ui';
// component edit-report
import FieldSelect from '../field-select';
import { withReportSortList } from './hocs';
//////////////////////////////////////////////////

const SortHeader = props => {
  const {
    theme,
    reportSortList,
    handleAddSortField,
  } = props;

  const sortLength = R.length(reportSortList);

  return (
    <Flex
      p='0 15px'
      height={40}
      borderBottom='1px solid'
      bg={theme.colors.bgColor}
      justifyContent='space-between'
      color={theme.colors.lightBlack}
      borderColor={theme.colors.middleGrey}
    >
      <Typography>{U.getTranslation('labels.sort', 'Sort')}</Typography>
      {
        R.lt(sortLength, 5)
        && (
        <Box
          fontSize={13}
          cursor='pointer'
          fontWeight={500}
          hoveredOpacity={0.5}
          onClick={handleAddSortField}
          color={theme.colors.success}
          title={U.getTranslation('actions.addField', 'Add Field')}
        >
          {U.getTranslation('actions.addField', 'Add Field')}
        </Box>
        )
      }
    </Flex>
  );
};

const FilterItemAction = props => {
  const {
    theme,
    fieldIndex,
    handleRemoveSortField,
  } = props;

  return (
    <Flex>
      <Box
        ml={10}
        cursor='grab'
        alignItems='center'
        title={U.getTranslation('actions.dragAndDrop', 'Drag and Drop')}
      >
        <DragIndicatorIcon size='25px' sx={{ color: theme.colors.success }} />
      </Box>
      <Box>
        <Flex
          ml={10}
          cursor='pointer'
          alignItems='center'
          title={U.getTranslation('actions.remove', 'Remove')}
          onClick={() => handleRemoveSortField(fieldIndex)}
        >
          <DeleteIcon size='25px' sx={{ color: theme.colors.success }} />
        </Flex>
      </Box>
    </Flex>
  );
};

const SortItem = props => {
  const {
    name,
    order,
    theme,
    zIndex,
    fieldIndex,
    availableFields,
    availableForSort,
    handleSelectSortName,
    handleChangeFieldOrder,
  } = props;

  const orderIcon = U.ifElse(
    R.equals(order, 'ASC'),
    <ArrowUpIcon size='25px' sx={{ color: theme.colors.success }} />,
    <ArrowDownIcon size='25px' sx={{ color: theme.colors.success }} />,
  );
  const orderToUpdate = U.ifElse(R.equals(order, 'ASC'), 'DESC', 'ASC');
  const field = availableFields?.find(af => R.equals(name, af?.value));

  let options = availableForSort;

  if (field) {
    options = R.concat([field], options);
  }

  return (
    <Flex py={10} zIndex={zIndex} alignItems='center'>
      <FilterItemAction {...props} />
      <FieldSelect
        value={name}
        theme={theme}
        options={options}
        fieldIndex={fieldIndex}
        action={handleSelectSortName}
        id={`report-sort-${fieldIndex}`}
      />
      <Flex pr={10}>
        <Box
          title={order}
          cursor='pointer'
          onClick={() => handleChangeFieldOrder({ fieldIndex, order: orderToUpdate })}
        >
          {orderIcon}
        </Box>
      </Flex>
    </Flex>
  );
};

const Fields = props => {
  const {
    theme,
    dndProcessing,
    reportSortList,
    availableFields,
    availableForSort,
    handleSelectSortName,
    handleRemoveSortField,
    handleChangeFieldOrder,
  } = props;

  const itemBorderColor = theme.colors.middleGrey;
  const border = U.ifElse(U.isTrue(dndProcessing), `1px dashed ${itemBorderColor}`, 'none');

  return useMemo(() => (
    <Flex border={border} flexDirection='column'>
      {
        reportSortList.map((field, index) => (
          <Draggable
            key={index}
            index={index}
            draggableId={`report-sort-list-dnd-item-${index}`}
          >
            {provided => (
              <Box
                width='100%'
                alignItems='start'
                border='1px solid'
                borderColor={itemBorderColor}
                zIndex={U.curriedParseInt(R.divide(150, R.inc(index)))}
              >
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                >
                  <SortItem
                    {...field}
                    theme={theme}
                    fieldIndex={index}
                    provided={provided}
                    reportSortList={reportSortList}
                    availableFields={availableFields}
                    availableForSort={availableForSort}
                    handleSelectSortName={handleSelectSortName}
                    handleRemoveSortField={handleRemoveSortField}
                    handleChangeFieldOrder={handleChangeFieldOrder}
                    name={field.name ? U.getTranslation(`labels.${field.name}`, field.name) : ''}
                  />
                </div>
                {provided.placeholder}
              </Box>
            )}
          </Draggable>
        ))
      }
    </Flex>
  ), [dndProcessing, reportSortList, availableFields]); // eslint-disable-line
};

const SortList = props => {
  const { theme, reportSortList } = props;

  const sortLength = R.keys(reportSortList);

  return (
    <Box width='auto'>
      <SortHeader {...props} />
      <Box>
        {
          U.isNilOrEmpty(sortLength) && (
            <Box p={15}>
              <Flex flexDirection='column'>
                <Typography fontSize={16} color={theme.colors.darkGrey}>
                  {U.getTranslation(
                    'errors.youDontHaveAnySortingForThisReport',
                    'You don\'t have any sorting for this Report',
                  )}
                </Typography>
              </Flex>
            </Box>
          )
          }
      </Box>
      {
          U.isNotNilAndNotEmpty(sortLength)
          && (
          <Droppable droppableId='sortFields'>
            {(provided, snapshot) => (
              <div ref={provided.innerRef}>
                <Fields snapshot={snapshot} {...props} />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          )
        }
    </Box>
  );
};

export default withReportSortList(SortList);
